import React, { createContext, useContext, useRef, useState, useEffect } from 'react';
import type { Song, PlayerState, PlayerContext as PlayerContextType, AudioQuality, SubscriptionTier } from '../types';
import { storage } from './api';
import { useAuth } from '../context/AuthContext';
import { AudioQualityService } from '../utils/audioQuality';
import { getAvailableQualities as getAvailableQualitiesForTier, DEFAULT_AUDIO_QUALITY } from '../constants/audioQuality';
import { analyticsTracker } from './AnalyticsTracker';

const PlayerContext = createContext<PlayerContextType | null>(null);

export function usePlayer() {
  const context = useContext(PlayerContext);
  if (!context) {
    throw new Error('usePlayer must be used within a PlayerProvider');
  }
  return context;
}

interface PlayerProviderProps {
  children: React.ReactNode;
}

export function PlayerProvider({ children }: PlayerProviderProps) {
  const { user } = useAuth();
  const [currentSong, setCurrentSong] = useState<Song | null>(null);
  const [queue, setQueue] = useState<Song[]>([]);
  const [queueHistory, setQueueHistory] = useState<Song[]>([]);
  const [state, setState] = useState<PlayerState>('paused');
  const [progress, setProgress] = useState(0);
  const [volume, setVolume] = useState(() => storage.getVolume());
  const [isShuffled, setIsShuffled] = useState(false);
  const [repeatMode, setRepeatMode] = useState<'none' | 'all' | 'one'>('none');
  const [duration, setDuration] = useState(0);
  const [isVideoMode, setIsVideoMode] = useState(false);
  const [authModalRequired, setAuthModalRequired] = useState(false);
  const [audioQuality, setAudioQualityState] = useState<AudioQuality>(() => 
    AudioQualityService.getPreferredQuality()
  );
  const [currentAudioUrl, setCurrentAudioUrl] = useState<string | null>(null);
  
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const progressInterval = useRef<number>();

  // Initialize audio element
  useEffect(() => {
    audioRef.current = new Audio();
    audioRef.current.volume = volume;

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      if (progressInterval.current) {
        window.clearInterval(progressInterval.current);
      }
    };
  }, []);

  // Handle volume changes
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
  }, [volume]);

  // Handle song changes
  useEffect(() => {
    if (!audioRef.current || !currentSong) return;
    
    // Get the appropriate audio URL based on quality and subscription
    const userTier: SubscriptionTier = user?.subscriptionTier || 'free';
    const selectedQuality = AudioQualityService.getBestAvailableQuality(
      currentSong, 
      userTier, 
      audioQuality
    );
    const audioUrl = AudioQualityService.getAudioUrlForQuality(currentSong, selectedQuality);
    
    if (!audioUrl) {
      console.error('No audio URL available for song:', currentSong.title);
      return;
    }
    
    setCurrentAudioUrl(audioUrl);
    audioRef.current.src = audioUrl;
    
    if (state === 'playing') {
      audioRef.current.play();
    }

    // Update progress
    if (progressInterval.current) {
      window.clearInterval(progressInterval.current);
    }
    progressInterval.current = window.setInterval(() => {
      if (audioRef.current) {
        const currentTime = audioRef.current.currentTime;
        const totalDuration = audioRef.current.duration;
        setProgress(currentTime / totalDuration);
        setDuration(totalDuration);
        
        // Update analytics tracking progress
        analyticsTracker.updateProgress(currentTime, totalDuration);
      }
    }, 1000);

    // Handle song end
    audioRef.current.onended = () => {
      if (repeatMode === 'one') {
        audioRef.current?.play();
      } else {
        // Handle next song logic
      }
    };

    // Handle duration change
    audioRef.current.onloadedmetadata = () => {
      setDuration(audioRef.current?.duration || 0);
    };
  }, [currentSong, audioQuality, user?.subscriptionTier]);

  const playSong = async (song: Song, songsQueue?: readonly Song[], startIndex?: number) => {
    // Check if user is authenticated
    if (!user) {
      setAuthModalRequired(true);
      return;
    }
    
    // Migrate legacy song format if needed
    const migratedSong = AudioQualityService.migrateLegacySong(song);
    
    // Start analytics tracking for the song
    analyticsTracker.startSongTracking(migratedSong, audioQuality);
    
    setCurrentSong(migratedSong);
    setState('playing');
    
    // If a queue is provided (like when playing a playlist), set it
    if (songsQueue && songsQueue.length > 0) {
      // Filter out the current song to avoid duplication and migrate songs
      const filteredQueue = songsQueue
        .filter(s => s.id !== song.id)
        .map(s => AudioQualityService.migrateLegacySong(s));
      setQueue([...filteredQueue]);
      setQueueHistory([]);
    }
  };

  const togglePlayPause = async () => {
    // Check if user is authenticated
    if (!user) {
      setAuthModalRequired(true);
      return;
    }
    
    if (!audioRef.current || !currentSong) return;

    if (state === 'playing') {
      audioRef.current.pause();
      setState('paused');
    } else {
      audioRef.current.play();
      setState('playing');
    }
  };

  const setPlayerVolume = (newVolume: number) => {
    setVolume(newVolume);
    storage.setVolume(newVolume);
  };

  const seek = (newProgress: number) => {
    if (!audioRef.current || !duration) return;
    const time = newProgress * duration;
    audioRef.current.currentTime = time;
    setProgress(newProgress);
  };

  const shuffleQueue = () => {
    setIsShuffled(!isShuffled);
    if (!isShuffled && queue.length > 0) {
      const newQueue = [...queue];
      for (let i = newQueue.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [newQueue[i], newQueue[j]] = [newQueue[j], newQueue[i]];
      }
      setQueue(newQueue);
    }
  };

  const playNext = async () => {
    if (!currentSong || queue.length === 0) return;
    
    // End current stream tracking
    await analyticsTracker.endStream(true);
    
    // Add current song to history
    setQueueHistory(prev => [...prev, currentSong]);
    
    // Get next song based on repeat mode
    if (repeatMode === 'one') {
      if (audioRef.current) {
        audioRef.current.currentTime = 0;
        audioRef.current.play();
      }
      // Restart analytics tracking for the same song
      analyticsTracker.startSongTracking(currentSong, audioQuality);
      return;
    }
    
    const nextSong = queue[0];
    const newQueue = queue.slice(1);
    
    if (nextSong) {
      const migratedSong = AudioQualityService.migrateLegacySong(nextSong);
      setCurrentSong(migratedSong);
      setQueue(newQueue);
      setState('playing');
    } else if (repeatMode === 'all') {
      // Reset queue and start from beginning
      setQueue(queueHistory);
      setQueueHistory([]);
      if (queueHistory.length > 0) {
        const migratedSong = AudioQualityService.migrateLegacySong(queueHistory[0]);
        setCurrentSong(migratedSong);
        setQueue(queueHistory.slice(1));
        setState('playing');
      }
    }
  };

  const playPrevious = async () => {
    if (!currentSong || queueHistory.length === 0) return;
    
    // End current stream tracking
    await analyticsTracker.endStream(false);
    
    // Add current song back to queue
    setQueue(prev => [currentSong, ...prev]);
    
    // Get previous song
    const previousSong = queueHistory[queueHistory.length - 1];
    const newHistory = queueHistory.slice(0, -1);
    
    if (previousSong) {
      const migratedSong = AudioQualityService.migrateLegacySong(previousSong);
      setCurrentSong(migratedSong);
      setQueueHistory(newHistory);
      setState('playing');
      
      // Start tracking the previous song
      analyticsTracker.startSongTracking(migratedSong, audioQuality);
    }
  };

  const toggleRepeat = () => {
    setRepeatMode(current => {
      switch (current) {
        case 'none': return 'all';
        case 'all': return 'one';
        case 'one': return 'none';
      }
    });
  };

  const toggleVideoMode = () => {
    setIsVideoMode(!isVideoMode);
  };

  const setAudioQuality = async (quality: AudioQuality) => {
    // Check if user can access this quality
    const userTier: SubscriptionTier = user?.subscriptionTier || 'free';
    if (!AudioQualityService.canUserAccessQuality(userTier, quality)) {
      console.warn(`User cannot access ${quality} quality with ${userTier} subscription`);
      return;
    }
    
    // Store the preference
    AudioQualityService.setPreferredQuality(quality);
    setAudioQualityState(quality);
    
    // If a song is currently loaded, update its audio URL
    if (currentSong && audioRef.current) {
      const newAudioUrl = AudioQualityService.getAudioUrlForQuality(currentSong, quality);
      if (newAudioUrl && newAudioUrl !== currentAudioUrl) {
        const wasPlaying = state === 'playing';
        const currentTime = audioRef.current.currentTime;
        
        audioRef.current.src = newAudioUrl;
        setCurrentAudioUrl(newAudioUrl);
        
        // Restart analytics tracking with new quality
        if (currentSong) {
          await analyticsTracker.endStream(false);
          analyticsTracker.startSongTracking(currentSong, quality);
        }
        
        // Restore playback position and state
        audioRef.current.onloadedmetadata = () => {
          if (audioRef.current) {
            audioRef.current.currentTime = currentTime;
            if (wasPlaying) {
              audioRef.current.play();
            }
          }
        };
      }
    }
  };

  const getAvailableQualities = (song: Song): AudioQuality[] => {
    const userTier: SubscriptionTier = user?.subscriptionTier || 'free';
    const songQualities = AudioQualityService.getSongAvailableQualities(song);
    const userAccessibleQualities = getAvailableQualitiesForTier(userTier);
    
    return songQualities.filter(quality => userAccessibleQualities.includes(quality));
  };

  const value: PlayerContextType = {
    currentSong,
    queue,
    state,
    progress,
    volume,
    isShuffled,
    repeatMode,
    duration,
    isVideoMode,
    authModalRequired,
    audioQuality,
    currentAudioUrl,
    playSong,
    togglePlayPause,
    setVolume: setPlayerVolume,
    seek,
    playNext,
    playPrevious,
    shuffleQueue,
    toggleRepeat,
    toggleVideoMode,
    setAudioQuality,
    getAvailableQualities,
    clearAuthModal: () => setAuthModalRequired(false),
  };

  return (
    <PlayerContext.Provider value={value}>
      {children}
    </PlayerContext.Provider>
  );
} 